import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { date } from "@/lib/format";
import { SelectBox, DemoTag } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function UserDialog({ open, onOpenChange, user, roles, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(user ? { name: user.name, role_id: user.role_id, active: user.active, password: "" } : { name: "", email: "", role_id: "", password: "" }); }, [open, user]);
  const save = async () => {
    try {
      const body = { ...f };
      if (!body.password) delete body.password;
      user ? await api.patch(`/users/${user.id}`, body) : await api.post("/users", body);
      toast.success(user ? "User updated" : "User created");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? `Edit ${user.name}` : "Add user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          {!user && <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>}
          <div className="space-y-1.5"><Label>Role</Label><SelectBox value={f.role_id} onChange={(v) => setF({ ...f, role_id: v })} testid="user-role-select" className="w-full" allLabel="Select role" options={roles.filter((r) => r.active).map((r) => ({ value: r.id, label: r.name }))} /></div>
          <div className="space-y-1.5"><Label>{user ? "Set new password (optional)" : "Initial password (optional — leave blank for Google sign-in only)"}</Label><Input type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></div>
          {user && <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" /></div>}
        </div>
        <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="user-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersTab() {
  const { refreshMeta } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
    setUsers(u.data);
    setRoles(r.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-slate-500">Deactivated users cannot sign in; their records and history are kept.</p>
        <Button onClick={() => setEdit({})} className="bg-orange-500 hover:bg-orange-600" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />Add user</Button>
      </div>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}<DemoTag show={u.is_demo} /></TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm">{u.role_name}</TableCell>
                <TableCell>{u.active ? <span className="text-xs text-emerald-700 font-semibold">Active</span> : <span className="text-xs text-slate-400">Deactivated</span>}</TableCell>
                <TableCell className="text-xs">{date(u.created_at)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UserDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} user={edit?.id ? edit : null} roles={roles} onSaved={() => { load(); refreshMeta(); }} />
    </div>
  );
}
